import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import {
  Plus,
  X,
  UserCog,
  Ban,
  Skull,
  Eye,
  EyeOff,
  AlertTriangle,
  MoreHorizontal,
  ArrowRightLeft,
  RotateCcw,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { PhoneInput } from '../../ui/PhoneInput';
import { LoadingSpinner } from '../../ui/Loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/Table';
import {
  useCdnAgents,
  useCreateCdnAgent,
  useSuspendCdnAgent,
  useUnsuspendCdnAgent,
  useSuperAgents,
  useAssignAgentToSuperAgent,
} from '../../../hooks/useApi';
import { usePermission } from '../../../hooks/useAuth';
import type { CdnAgent, SuperAgent } from '../../../types';
import { costAsPercent, isChildCostRateValid, normalizeCostRate, percentInputToRate } from './agentCdnUtils';
import {
  CDN_AGENT_USERNAME_MAX_LENGTH,
  getCdnAgentUsernameError,
  normalizeCdnAgentUsername,
} from '../../../utils/username';
import { PageHeader } from '@kioskgaming/ui';
import CommonModal from '../../ui/CommonModal';
import TerminateAgentModal from './TerminateAgentModal';

const PASSWORD_MIN = 8;

const SUSPEND_CONFIRM_WORD = 'suspended';
const MANAGE_MENU_GAP = 8;
const MANAGE_MENU_VIEWPORT_PADDING = 8;
const MANAGE_MENU_ITEM_HEIGHT = 36;
const MANAGE_MENU_VERTICAL_PADDING = 8;

function estimateManageMenuHeight(
  agent: CdnAgent,
  canManage: boolean,
  superAgentCount: number,
): number {
  let items = 1;
  if (canManage && superAgentCount > 0) items += 1;
  if (canManage && agent.status === 'active') items += 1;
  if (canManage && agent.status === 'suspended') items += 1;
  if (canManage && (agent.status === 'active' || agent.status === 'suspended')) items += 1;
  return items * MANAGE_MENU_ITEM_HEIGHT + MANAGE_MENU_VERTICAL_PADDING;
}

function computeManageMenuPlacement(
  rect: DOMRect,
  menuHeight: number,
): { top: number; left: number; origin: 'top' | 'bottom' } {
  const spaceBelow = window.innerHeight - rect.bottom - MANAGE_MENU_GAP;
  const spaceAbove = rect.top - MANAGE_MENU_GAP;
  const openAbove = menuHeight > spaceBelow && spaceAbove >= spaceBelow;

  const left = Math.min(
    Math.max(rect.right, MANAGE_MENU_VIEWPORT_PADDING),
    window.innerWidth - MANAGE_MENU_VIEWPORT_PADDING,
  );

  if (openAbove) {
    const anchorY = Math.max(
      rect.top - MANAGE_MENU_GAP,
      MANAGE_MENU_VIEWPORT_PADDING + menuHeight,
    );
    return { top: anchorY, left, origin: 'bottom' };
  }

  const top = Math.min(
    Math.max(rect.bottom + MANAGE_MENU_GAP, MANAGE_MENU_VIEWPORT_PADDING),
    window.innerHeight - menuHeight - MANAGE_MENU_VIEWPORT_PADDING,
  );
  return { top, left, origin: 'top' };
}

const AgentManagement: React.FC = () => {
  const canManage = usePermission('agentManagement');
  const canManagePopupPermissions = usePermission('agentPopupPermissionManagement');
  const { data, isLoading, error, isFetching } = useCdnAgents(canManage);
  const { data: superAgentsData } = useSuperAgents(canManage);
  const createAgent = useCreateCdnAgent();
  const suspendAgent = useSuspendCdnAgent();
  const unsuspendAgent = useUnsuspendCdnAgent();
  const assignAgentToSuperAgent = useAssignAgentToSuperAgent();

  const [createOpen, setCreateOpen] = useState(false);
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [suspendTarget, setSuspendTarget] = useState<CdnAgent | null>(null);
  const [suspendConfirmText, setSuspendConfirmText] = useState('');
  const [terminateTarget, setTerminateTarget] = useState<CdnAgent | null>(null);
  const [unsuspendTarget, setUnsuspendTarget] = useState<CdnAgent | null>(null);
  const [assignTarget, setAssignTarget] = useState<CdnAgent | null>(null);
  const [assignSuperAgentId, setAssignSuperAgentId] = useState('');
  const [assignError, setAssignError] = useState<string | null>(null);
  const [createCostError, setCreateCostError] = useState<string | null>(null);
  const [createContactError, setCreateContactError] = useState<string | null>(null);
  const [manageMenu, setManageMenu] = useState<{ id: CdnAgent['id']; anchorRect: DOMRect } | null>(null);
  const [selectedSuperAgentFilter, setSelectedSuperAgentFilter] = useState<string>('all');
  const [emailSearch, setEmailSearch] = useState('');
  const [manageMenuPlacement, setManageMenuPlacement] = useState<{
    top: number;
    left: number;
    origin: 'top' | 'bottom';
  } | null>(null);
  const [form, setForm] = useState({
    super_agent_id: '',
    name: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    cost_percent: '0',
    can_create_shop_popup: false,
    can_manage_shop_popup_permission: false,
    default_shop_can_create_player_popup: false,
  });

  const manageButtonRefs = React.useRef(new Map<string, HTMLElement | null>());
  const manageMenuRef = useRef<HTMLDivElement | null>(null);

  const agents = useMemo(() => data?.data ?? [], [data]);
  const activeSuperAgents = useMemo(
    () => (superAgentsData?.data ?? []).filter((sa) => sa.status === 'active'),
    [superAgentsData],
  );
  const selectedSuperAgentForCreate: SuperAgent | undefined = useMemo(
    () => activeSuperAgents.find((sa) => sa.id === form.super_agent_id),
    [activeSuperAgents, form.super_agent_id],
  );
  const createCostPercentValue = parseFloat(form.cost_percent);
  const createCostPercentOk =
    Number.isFinite(createCostPercentValue) &&
    createCostPercentValue >= 0 &&
    createCostPercentValue < 100;
  const proposedAgentCostRate = percentInputToRate(form.cost_percent);
  const parentSuperAgentCostRate = normalizeCostRate(selectedSuperAgentForCreate?.costRate);
  const createCostHierarchyOk =
    !form.super_agent_id.trim() ||
    (!!selectedSuperAgentForCreate &&
      isChildCostRateValid(parentSuperAgentCostRate, proposedAgentCostRate));
  const superAgentNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of superAgentsData?.data ?? []) {
      map.set(item.id, item.name);
    }
    return map;
  }, [superAgentsData]);
  const filteredAgents = useMemo(() => {
    const normalizedEmailSearch = emailSearch.trim().toLowerCase();
    return agents.filter((agent) => {
      const superAgentMatched =
        selectedSuperAgentFilter === 'all'
          ? true
          : selectedSuperAgentFilter === 'unassigned'
            ? !agent.superAgentId
            : agent.superAgentId === selectedSuperAgentFilter;
      const emailMatched = normalizedEmailSearch
        ? agent.email.toLowerCase().includes(normalizedEmailSearch)
        : true;
      return superAgentMatched && emailMatched;
    });
  }, [agents, emailSearch, selectedSuperAgentFilter]);

  const resetForm = () => {
    setForm({
      super_agent_id: '',
      name: '',
      username: '',
      email: '',
      phone: '',
      password: '',
      cost_percent: '0',
      can_create_shop_popup: false,
      can_manage_shop_popup_permission: false,
      default_shop_can_create_player_popup: false,
    });
    setShowCreatePassword(false);
    setCreateCostError(null);
    setCreateContactError(null);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManage) return;
    if (!form.super_agent_id.trim()) return;
    if (!selectedSuperAgentForCreate) return;
    if (form.password.length < PASSWORD_MIN) return;
    const email = form.email.trim();
    const phone = form.phone.trim();
    if (!email && !phone) {
      const message = 'Email or phone is required. You only need to provide one.';
      setCreateContactError(message);
      toast.error(message);
      return;
    }
    const usernameError = getCdnAgentUsernameError(form.username);
    if (usernameError) {
      toast.error(usernameError);
      return;
    }
    const username = normalizeCdnAgentUsername(form.username).trim();
    if (!createCostPercentOk) {
      setCreateCostError('Cost rate must be at least 0% and less than 100%.');
      return;
    }
    if (!isChildCostRateValid(parentSuperAgentCostRate, proposedAgentCostRate)) {
      setCreateCostError(
        `Agent cost rate must be at least super agent cost (${(parentSuperAgentCostRate * 100).toFixed(2)}%).`,
      );
      return;
    }
    setCreateCostError(null);
    setCreateContactError(null);
    try {
      await createAgent.mutateAsync({
        super_agent_id: form.super_agent_id.trim(),
        name: form.name.trim(),
        username,
        email: email || undefined,
        phone: phone || undefined,
        password: form.password,
        cost_rate: percentInputToRate(form.cost_percent),
        can_create_shop_popup: canManagePopupPermissions ? form.can_create_shop_popup : false,
        can_manage_shop_popup_permission: canManagePopupPermissions
          ? form.can_manage_shop_popup_permission
          : false,
        default_shop_can_create_player_popup: canManagePopupPermissions
          ? form.default_shop_can_create_player_popup
          : false,
      });
      resetForm();
      setCreateOpen(false);
    } catch {
      // Failure handled by useCreateCdnAgent onError (toast with API message)
    }
  };

  const closeSuspendModal = () => {
    setSuspendTarget(null);
    setSuspendConfirmText('');
  };

  const closeTerminateModal = () => {
    setTerminateTarget(null);
  };
  const closeAssignModal = () => {
    setAssignTarget(null);
    setAssignSuperAgentId('');
    setAssignError(null);
  };

  const closeUnsuspendModal = () => {
    if (unsuspendAgent.isLoading) return;
    setUnsuspendTarget(null);
  };

  const suspendPhraseOk =
    suspendConfirmText.trim().toLowerCase() === SUSPEND_CONFIRM_WORD;
  const handleSuspendConfirmed = async () => {
    if (!suspendTarget || !suspendPhraseOk) return;
    try {
      await suspendAgent.mutateAsync(suspendTarget.id);
      closeSuspendModal();
    } catch {
      // useSuspendCdnAgent onError shows toast
    }
  };

  const handleUnsuspendConfirmed = async () => {
    if (!unsuspendTarget) return;
    try {
      await unsuspendAgent.mutateAsync(unsuspendTarget.id);
      closeUnsuspendModal();
    } catch {
      // useUnsuspendCdnAgent onError shows toast
    }
  };

  const handleAssignConfirmed = async () => {
    if (!assignTarget || !assignSuperAgentId) return;
    setAssignError(null);
    try {
      await assignAgentToSuperAgent.mutateAsync({
        superAgentId: assignSuperAgentId,
        agentId: assignTarget.id,
      });
      closeAssignModal();
    } catch (error: unknown) {
      const rawMessage =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      if (typeof rawMessage === 'string' && rawMessage.trim()) {
        setAssignError(rawMessage);
      } else {
        setAssignError('Unable to move this agent to the selected super agent. Please try again.');
      }
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest('[data-manage-menu-root="true"]')) {
        setManageMenu(null);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (!manageMenu) return;

    const updateRect = () => {
      const button = manageButtonRefs.current.get(String(manageMenu.id));
      if (!button) return;
      setManageMenu((current) => (current ? { ...current, anchorRect: button.getBoundingClientRect() } : current));
    };

    window.addEventListener('resize', updateRect);
    // capture scroll from any scroll container
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [manageMenu]);

  useEffect(() => {
    if (!manageMenu) {
      setManageMenuPlacement(null);
      return;
    }
    const agent = filteredAgents.find((item) => item.id === manageMenu.id);
    if (!agent) {
      setManageMenuPlacement(null);
      return;
    }
    const menuHeight = estimateManageMenuHeight(
      agent,
      canManage,
      superAgentsData?.data?.length ?? 0,
    );
    setManageMenuPlacement(computeManageMenuPlacement(manageMenu.anchorRect, menuHeight));
  }, [manageMenu, canManage, filteredAgents, superAgentsData]);

  useLayoutEffect(() => {
    if (!manageMenu || !manageMenuRef.current) return;
    const measuredHeight = manageMenuRef.current.offsetHeight;
    if (measuredHeight <= 0) return;
    const next = computeManageMenuPlacement(manageMenu.anchorRect, measuredHeight);
    setManageMenuPlacement((current) => {
      if (
        current &&
        current.top === next.top &&
        current.left === next.left &&
        current.origin === next.origin
      ) {
        return current;
      }
      return next;
    });
  }, [manageMenu, manageMenuPlacement]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Agent Management"
        description="Create and govern CDN agent accounts"
        actions={
          canManage ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create agent
            </Button>
          ) : null
        }
      />

      {canManage && createOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0"
          onClick={(e) => {
            if (e.target === e.currentTarget && !createAgent.isLoading) {
              setCreateOpen(false);
              resetForm();
            }
          }}
        >
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <UserCog className="h-5 w-5 text-primary" />
                New agent
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCreateOpen(false);
                  resetForm();
                }}
                disabled={createAgent.isLoading}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="create-agent-super-agent" className="text-sm font-medium">
                    Super agent
                  </label>
                  <select
                    id="create-agent-super-agent"
                    required
                    value={form.super_agent_id}
                    onChange={(ev) => {
                      setCreateCostError(null);
                      setForm((f) => ({ ...f, super_agent_id: ev.target.value }));
                    }}
                    disabled={createAgent.isLoading}
                    className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Select super agent</option>
                    {activeSuperAgents.map((sa) => (
                      <option key={sa.id} value={sa.id}>
                        {sa.name} ({sa.email})
                      </option>
                    ))}
                  </select>
                  {activeSuperAgents.length === 0 ? (
                    <p className="text-xs text-destructive">No active super agents. Create one before adding an agent.</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Required — new agent is created under this super agent.</p>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Name</label>
                  <Input
                    required
                    value={form.name}
                    onChange={(ev) => setForm((f) => ({ ...f, name: ev.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="create-agent-username">
                    Username
                  </label>
                  <Input
                    id="create-agent-username"
                    required
                    autoComplete="username"
                    placeholder="Login username"
                    maxLength={CDN_AGENT_USERNAME_MAX_LENGTH}
                    value={form.username}
                    onChange={(ev) =>
                      setForm((f) => ({
                        ...f,
                        username: normalizeCdnAgentUsername(ev.target.value).slice(0, CDN_AGENT_USERNAME_MAX_LENGTH),
                      }))
                    }
                    disabled={createAgent.isLoading}
                  />
                  <p className="text-xs text-muted-foreground">
                    Lowercase letters, numbers, and <span className="font-medium">_</span>; must start with a letter.
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="create-agent-email">
                    Email
                  </label>
                  <Input
                    id="create-agent-email"
                    type="email"
                    autoComplete="email"
                    placeholder="Email address"
                    value={form.email}
                    onChange={(ev) => {
                      setCreateContactError(null);
                      setForm((f) => ({ ...f, email: ev.target.value }));
                    }}
                    disabled={createAgent.isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="create-agent-phone">
                    Phone
                  </label>
                  <PhoneInput
                    id="create-agent-phone"
                    value={form.phone}
                    onChange={(phone) => {
                      setCreateContactError(null);
                      setForm((f) => ({ ...f, phone }));
                    }}
                    disabled={createAgent.isLoading}
                    placeholder="Phone number"
                  />
                  {createContactError ? (
                    <p className="text-xs text-destructive" role="alert">
                      {createContactError}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Email or phone is required — you only need to fill in one of them.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Password</label>
                  <div className="relative">
                    <Input
                      required
                      type={showCreatePassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={form.password}
                      onChange={(ev) => setForm((f) => ({ ...f, password: ev.target.value }))}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCreatePassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showCreatePassword ? 'Hide password' : 'Show password'}
                    >
                      {showCreatePassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">At least {PASSWORD_MIN} characters.</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Cost rate (%)</label>
                  {selectedSuperAgentForCreate ? (
                    <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
                      <span className="text-muted-foreground">Super agent cost rate (reference): </span>
                      <span className="font-semibold text-foreground">
                        {costAsPercent(selectedSuperAgentForCreate)}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1.5 leading-snug">
                        Agent cost must be <span className="font-medium text-foreground">at least</span> this value
                        (hierarchy rule) and <span className="font-medium text-foreground">under 100%</span>.
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Select a super agent to see its cost rate.</p>
                  )}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        max={99.99}
                        value={form.cost_percent}
                        onChange={(ev) => {
                          setCreateCostError(null);
                          setForm((f) => ({ ...f, cost_percent: ev.target.value }));
                        }}
                        className="w-full"
                      />
                    </div>
                    <span className="text-sm text-muted-foreground shrink-0 font-medium">%</span>
                  </div>
                  {createCostError ? (
                    <p className="text-xs text-destructive" role="alert">
                      {createCostError}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Enter a percentage between 0 and under 100 (e.g. 92.5 for 92.5%).
                    </p>
                  )}
                </div>
                {canManagePopupPermissions && (
                  <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3">
                    <p className="text-sm font-medium">In-app popups / broadcasts (delegated)</p>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={form.can_create_shop_popup}
                        onChange={(ev) =>
                          setForm((f) => ({ ...f, can_create_shop_popup: ev.target.checked }))
                        }
                      />
                      <span className="text-sm">
                        <span className="font-medium">Allow this agent to send popups/broadcasts to shops/players</span>
                        <span className="block text-xs text-muted-foreground mt-0.5">
                          When enabled, the agent can send delegated popups/broadcasts.
                        </span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={form.can_manage_shop_popup_permission}
                        onChange={(ev) =>
                          setForm((f) => ({
                            ...f,
                            can_manage_shop_popup_permission: ev.target.checked,
                          }))
                        }
                      />
                      <span className="text-sm">
                        <span className="font-medium">Allow this agent to manage popup permission for its shops</span>
                        <span className="block text-xs text-muted-foreground mt-0.5">
                          Agent can enable/disable shop popup permission per shop.
                        </span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={form.default_shop_can_create_player_popup}
                        onChange={(ev) =>
                          setForm((f) => ({
                            ...f,
                            default_shop_can_create_player_popup: ev.target.checked,
                          }))
                        }
                      />
                      <span className="text-sm">
                        <span className="font-medium">Default: new shops can send popups/broadcasts to players</span>
                        <span className="block text-xs text-muted-foreground mt-0.5">
                          When the agent creates a shop, that shop can inherit this default. You can override
                          per shop later.
                        </span>
                      </span>
                    </label>
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setCreateOpen(false);
                      resetForm();
                    }}
                    disabled={createAgent.isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      createAgent.isLoading ||
                      form.password.length < PASSWORD_MIN ||
                      !form.super_agent_id.trim() ||
                      activeSuperAgents.length === 0 ||
                      !createCostPercentOk ||
                      !createCostHierarchyOk
                    }
                  >
                    {createAgent.isLoading ? 'Creating…' : 'Create'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {suspendTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0"
          onClick={(e) => {
            if (e.target === e.currentTarget && !suspendAgent.isLoading) closeSuspendModal();
          }}
        >
          <Card
            className="w-full max-w-md border-amber-500/40 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="space-y-1">
              <CardTitle className="text-lg flex items-center gap-2 text-amber-900 dark:text-amber-200">
                <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
                Suspend agent?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className="rounded-md border border-amber-500/50 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm text-amber-950 dark:text-amber-100"
                role="alert"
              >
                <p className="font-semibold">Warning — important action</p>
                <p className="mt-2 text-amber-900/90 dark:text-amber-100/90">
                  Suspending <span className="font-medium">{suspendTarget.email}</span> blocks the agent from normal
                  operation. Only proceed if you intend to suspend this account.
                </p>
              </div>
              <div className="space-y-2">
                <label htmlFor="confirm-suspend-agent" className="text-sm font-medium">
                  Type <span className="font-mono text-foreground">{SUSPEND_CONFIRM_WORD}</span> to confirm
                </label>
                <Input
                  id="confirm-suspend-agent"
                  autoComplete="off"
                  value={suspendConfirmText}
                  onChange={(ev) => setSuspendConfirmText(ev.target.value)}
                  placeholder={SUSPEND_CONFIRM_WORD}
                  className="font-mono"
                  disabled={suspendAgent.isLoading}
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={closeSuspendModal} disabled={suspendAgent.isLoading}>
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  className="border-amber-600 text-amber-900 bg-amber-100 hover:bg-amber-200 dark:bg-amber-950 dark:text-amber-100 dark:hover:bg-amber-900/80 dark:border-amber-500 font-semibold"
                  onClick={handleSuspendConfirmed}
                  disabled={suspendAgent.isLoading || !suspendPhraseOk}
                >
                  {suspendAgent.isLoading ? 'Working…' : 'Suspend agent'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <TerminateAgentModal
        agent={
          terminateTarget
            ? { id: terminateTarget.id, email: terminateTarget.email, name: terminateTarget.name }
            : null
        }
        isOpen={!!terminateTarget}
        onClose={closeTerminateModal}
      />

      <CommonModal
        isOpen={!!unsuspendTarget}
        onClose={closeUnsuspendModal}
        size="md"
        closeDisabled={unsuspendAgent.isLoading}
        closeOnOverlayClick={!unsuspendAgent.isLoading}
        title={
          <span className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 shrink-0 text-emerald-600" aria-hidden />
            Reactivate this suspended agent?
          </span>
        }
        footer={
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
            <Button variant="outline" onClick={closeUnsuspendModal} disabled={unsuspendAgent.isLoading}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              className="border-emerald-600 bg-emerald-50 font-semibold text-emerald-900 hover:bg-emerald-100 dark:border-emerald-500 dark:bg-emerald-950 dark:text-emerald-100 dark:hover:bg-emerald-900/80"
              onClick={() => void handleUnsuspendConfirmed()}
              disabled={unsuspendAgent.isLoading}
            >
              {unsuspendAgent.isLoading ? 'Working…' : 'Reactivate agent'}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-muted-foreground">
          This restores normal portal access for{' '}
          <span className="font-medium text-foreground">{unsuspendTarget?.email ?? 'this agent'}</span>.
        </p>
      </CommonModal>

      {assignTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !assignAgentToSuperAgent.isLoading) closeAssignModal();
          }}
        >
          <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="space-y-1">
              <CardTitle className="text-lg flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5 text-primary" />
                Move to super agent
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Select a super agent to manage <span className="font-medium text-foreground">{assignTarget.email}</span>.
              </p>
              <div className="space-y-2">
                <label htmlFor="assign-super-agent-select" className="text-sm font-medium">
                  Super agent
                </label>
                <select
                  id="assign-super-agent-select"
                  value={assignSuperAgentId}
                  onChange={(e) => {
                    setAssignSuperAgentId(e.target.value);
                    setAssignError(null);
                  }}
                  disabled={assignAgentToSuperAgent.isLoading}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select super agent</option>
                  {(superAgentsData?.data ?? []).map((sa) => (
                    <option key={sa.id} value={sa.id}>
                      {sa.name} ({sa.email})
                    </option>
                  ))}
                </select>
              </div>
              {assignError ? <p className="text-sm text-destructive">{assignError}</p> : null}
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  onClick={closeAssignModal}
                  disabled={assignAgentToSuperAgent.isLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAssignConfirmed}
                  disabled={assignAgentToSuperAgent.isLoading || !assignSuperAgentId}
                >
                  {assignAgentToSuperAgent.isLoading ? 'Moving…' : 'OK'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b bg-muted/20">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <label htmlFor="agent-super-agent-filter" className="text-sm font-medium">
                  Super agent
                </label>
                <select
                  id="agent-super-agent-filter"
                  value={selectedSuperAgentFilter}
                  onChange={(e) => setSelectedSuperAgentFilter(e.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="all">All super agents</option>
                  <option value="unassigned">Unassigned</option>
                  {(superAgentsData?.data ?? []).map((sa) => (
                    <option key={sa.id} value={sa.id}>
                      {sa.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label htmlFor="agent-email-search" className="text-sm font-medium">
                  Search email
                </label>
                <Input
                  id="agent-email-search"
                  type="search"
                  value={emailSearch}
                  onChange={(e) => setEmailSearch(e.target.value)}
                  placeholder="Enter agent email"
                />
              </div>
            </div>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center h-48">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="p-6 text-center text-red-600">Failed to load agents.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table containerClassName="overflow-visible">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Super agent</TableHead>
                    <TableHead className="w-[96px]">Cost (%)</TableHead>
                    <TableHead className="w-[120px]">Status</TableHead>
                    <TableHead className="text-right min-w-[120px] w-[14%]">Manage</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAgents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                        No agents found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAgents.map((agent) => (
                      <TableRow key={agent.id}>
                        <TableCell>
                          <Link
                            to={`/agents/${agent.id}`}
                            className="font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                          >
                            {agent.name}
                          </Link>
                        </TableCell>
                        <TableCell>{agent.email}</TableCell>
                        <TableCell>
                          {agent.superAgentId ? superAgentNameById.get(agent.superAgentId) || '—' : '—'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">{costAsPercent(agent)}</TableCell>
                        <TableCell>
                          <span
                            className={
                              agent.status === 'active'
                                ? 'inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium uppercase tracking-wide'
                                : agent.status === 'suspended'
                                  ? 'inline-flex items-center px-2 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-medium uppercase tracking-wide'
                                  : 'inline-flex items-center px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium uppercase tracking-wide'
                            }
                          >
                            {agent.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-right align-middle">
                          <div className="relative inline-flex justify-end" data-manage-menu-root="true">
                            <span
                              ref={(el: HTMLSpanElement | null) => {
                                manageButtonRefs.current.set(String(agent.id), el);
                              }}
                              className="inline-flex"
                            >
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                title="Open actions"
                                aria-label="Open actions"
                                className="h-8 w-8 p-0"
                                onClick={(e) => {
                                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                  setManageMenu((current) =>
                                    current?.id === agent.id ? null : { id: agent.id, anchorRect: rect },
                                  );
                                }}
                              >
                                <MoreHorizontal className="h-4 w-4" aria-hidden />
                              </Button>
                            </span>

                            {manageMenu?.id === agent.id && manageMenuPlacement
                              ? createPortal(
                                  <div
                                    ref={manageMenuRef}
                                    data-manage-menu-root="true"
                                    className="fixed z-[9999] min-w-[170px] rounded-md border bg-background p-1 shadow-lg"
                                    style={{
                                      top: manageMenuPlacement.top,
                                      left: manageMenuPlacement.left,
                                      transform:
                                        manageMenuPlacement.origin === 'top'
                                          ? 'translateX(-100%)'
                                          : 'translate(-100%, -100%)',
                                    }}
                                    onClick={(ev) => ev.stopPropagation()}
                                  >
                                    <Link
                                      to={`/agents/${agent.id}`}
                                      className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-muted"
                                      onClick={() => setManageMenu(null)}
                                    >
                                      <Eye className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                      View
                                    </Link>

                                    {canManage && (superAgentsData?.data?.length ?? 0) > 0 && (
                                      <button
                                        type="button"
                                        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                                        onClick={() => {
                                          setManageMenu(null);
                                          setAssignTarget(agent);
                                          setAssignSuperAgentId(agent.superAgentId || '');
                                          setAssignError(null);
                                        }}
                                        disabled={
                                          assignAgentToSuperAgent.isLoading ||
                                          !!suspendTarget ||
                                          !!terminateTarget ||
                                          !!unsuspendTarget ||
                                          unsuspendAgent.isLoading
                                        }
                                      >
                                        <ArrowRightLeft className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                        Move to super agent
                                      </button>
                                    )}

                                    {canManage && agent.status === 'active' && (
                                      <button
                                        type="button"
                                        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-amber-900 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-amber-100 dark:hover:bg-amber-950/60"
                                        onClick={() => {
                                          setManageMenu(null);
                                          setSuspendConfirmText('');
                                          setSuspendTarget(agent);
                                        }}
                                        disabled={
                                          suspendAgent.isLoading ||
                                          !!suspendTarget ||
                                          !!terminateTarget ||
                                          !!unsuspendTarget ||
                                          unsuspendAgent.isLoading
                                        }
                                      >
                                        <Ban className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                        Suspend
                                      </button>
                                    )}

                                    {canManage && agent.status === 'suspended' && (
                                      <button
                                        type="button"
                                        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-emerald-900 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-emerald-100 dark:hover:bg-emerald-950/60"
                                        onClick={() => {
                                          setManageMenu(null);
                                          setUnsuspendTarget(agent);
                                        }}
                                        disabled={
                                          unsuspendAgent.isLoading ||
                                          !!suspendTarget ||
                                          !!terminateTarget ||
                                          !!unsuspendTarget ||
                                          suspendAgent.isLoading
                                        }
                                      >
                                        <RotateCcw className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                        Unsuspend
                                      </button>
                                    )}

                                    {canManage && (agent.status === 'active' || agent.status === 'suspended') && (
                                      <button
                                        type="button"
                                        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-destructive hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-red-950/50"
                                        onClick={() => {
                                          setManageMenu(null);
                                          setTerminateTarget(agent);
                                        }}
                                        disabled={
                                          !!suspendTarget ||
                                          !!terminateTarget ||
                                          !!unsuspendTarget ||
                                          unsuspendAgent.isLoading
                                        }
                                      >
                                        <Skull className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                        Terminate
                                      </button>
                                    )}
                                  </div>,
                                  document.body,
                                )
                              : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
        <div className="px-6 py-3 text-sm text-muted-foreground border-t">
          Total: <span className="font-medium text-foreground">{filteredAgents.length}</span>
          {isFetching && !isLoading && <span className="ml-2">(Refreshing…)</span>}
        </div>
      </Card>
    </div>
  );
};

export default AgentManagement;
